/**
 * Per-object encode/decode for the persistence layer.
 *
 * This is the only place where the live `SceneObject` shape meets the
 * serialized `SerializedSceneObject` shape. Every persistence path
 * (.pr files, IndexedDB cache, session restore, library objects/collections)
 * routes per-object work through `encodeSceneObject` / `decodeSceneObject`.
 *
 * Worker-safe: no DOM, no store imports, no globals.
 */

import { generateCurveGeometry } from "../../modeling/curveGeometry.ts";
import { getGenerator } from "../../modeling/generators/registry.ts";
import type {
  CollectionGeneratorRef,
  GeneratorListItem,
  GeneratorParamSpec,
  GeneratorParams,
  GeneratorParamValue,
  GeneratorRef,
} from "../../modeling/generators/types.ts";
import { createListItemId } from "../../modeling/generators/types.ts";
import { normalizeSceneObjectGeometry } from "../../modeling/topologyBackedGeometry.ts";
import type {
  FaceAttributeTable,
  MaterialSlot,
  PolygonTopology,
  SceneMaterial,
  SceneObject,
} from "../../modeling/types.ts";
import {
  asLoopNormals,
  asRenderTriangleIndices,
  DEFAULT_MATERIAL,
  EMPTY_RENDER_TRIANGLE_INDICES,
  normalizeFaceAttributes,
} from "../../modeling/types.ts";
import {
  base64ToFloat32Array,
  base64ToUint8Array,
  base64ToUint16Array,
  base64ToUint32Array,
  typedArrayToBase64,
  uint8ArrayToBase64,
} from "../encoding.ts";
import type {
  SerializedFaceAttributeTable,
  SerializedGeneratorRef,
  SerializedSceneObject,
  SerializedTopology,
} from "./types.ts";

// ---------------------------------------------------------------------------
// Material default detection
// ---------------------------------------------------------------------------

/**
 * True when a material is structurally identical to `DEFAULT_MATERIAL` and
 * can be omitted from serialization. The decoder restores `DEFAULT_MATERIAL`
 * when it sees a null material.
 */
export function isDefaultMaterial(m: SceneMaterial): boolean {
  return (
    m.color === DEFAULT_MATERIAL.color &&
    m.roughness === DEFAULT_MATERIAL.roughness &&
    m.metalness === DEFAULT_MATERIAL.metalness &&
    m.emissiveColor == null &&
    m.emissiveIntensity == null &&
    m.normalIntensity == null &&
    m.opacity == null &&
    m.specularIntensity == null &&
    m.clearcoat == null &&
    m.clearcoatRoughness == null &&
    m.sheen == null &&
    m.sheenRoughness == null &&
    m.sheenColor == null &&
    m.gridPattern == null &&
    m.gridCellSize == null &&
    m.gridOpacity == null &&
    m.gridColor == null &&
    m.gridLines == null &&
    m.gridRotation == null &&
    m.gridGroutThickness == null &&
    m.gridBumpScale == null &&
    m.gridProjection == null &&
    m.transmission == null &&
    m.ior == null &&
    m.thickness == null
  );
}

// ---------------------------------------------------------------------------
// Mannequin geometry omission
// ---------------------------------------------------------------------------

/**
 * True when a mannequin's geometry is reconstructed from the GLB at load time
 * and should therefore be omitted from the serialized form.
 */
export function mannequinCanOmitGeometry(o: {
  objectKind: string;
  mannequinModelId?: string;
  vertices: Float32Array;
  baseVertices?: Float32Array;
}): boolean {
  return o.objectKind === "mannequin" && o.mannequinModelId != null;
}

// ---------------------------------------------------------------------------
// Generator ref codec — additive-tolerant loader
// ---------------------------------------------------------------------------

/**
 * Recursively encode a params record as plain JSON. Handles nested list
 * items with their own params, so the entire schema (including compound
 * generators with list-typed conditional parameters) serializes uniformly.
 */
function encodeParams(params: GeneratorParams): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(params)) {
    if (Array.isArray(v)) {
      out[k] = (v as readonly GeneratorListItem[]).map((item) => ({
        id: item.id,
        itemType: item.itemType,
        params: encodeParams(item.params),
      }));
    } else {
      out[k] = v;
    }
  }
  return out;
}

/** Encode a live single-mesh `GeneratorRef` to its serialized form. */
function encodeGeneratorRef(ref: GeneratorRef): SerializedGeneratorRef {
  const out: SerializedGeneratorRef = {
    generatorId: ref.generatorId,
    params: encodeParams(ref.params),
  };
  if (ref.geometrySignature != null)
    out.geometrySignature = ref.geometrySignature;
  return out;
}

/**
 * Encode a collection-owned compound generator ref. The `roleToObjectId`
 * map is emitted as a plain object, and the nested params are recursively
 * encoded via the same helper used for single-mesh refs.
 */
export function encodeCollectionGeneratorRef(ref: CollectionGeneratorRef): {
  generatorId: string;
  params: Record<string, unknown>;
  roleToObjectId: Record<string, string>;
} {
  return {
    generatorId: ref.generatorId,
    params: encodeParams(ref.params),
    roleToObjectId: { ...ref.roleToObjectId },
  };
}

/**
 * Recursively decode a params record against a schema, reconciling each
 * key against its spec:
 *
 *  - Number / boolean / enum: overlay the raw value when it type-matches
 *    and (for enums) is a valid option; otherwise fall back to the spec
 *    default.
 *  - List: drop items whose subtype is unknown, drop items whose params
 *    fail to decode, fill missing item-level params from the subtype's
 *    defaults. The whole list falls back to the spec default when the
 *    incoming value isn't an array.
 *  - Section: recurse into the section's child specs. Sections are purely
 *    structural and do not own a value slot in `GeneratorParams`.
 *  - Unknown spec keys are silently dropped (additive-tolerant).
 *
 * This preserves old saves across schema growth: adding a new param or
 * item type never breaks load; removing one never corrupts on save.
 */
function decodeParams(
  specs: readonly GeneratorParamSpec[],
  raw: Record<string, unknown> | null | undefined,
): GeneratorParams {
  const source: Record<string, unknown> =
    raw && typeof raw === "object" ? raw : {};
  const out: { [key: string]: GeneratorParamValue } = {};

  for (const spec of specs) {
    const v = source[spec.id];
    switch (spec.type) {
      case "number":
        out[spec.id] =
          typeof v === "number" && Number.isFinite(v) ? v : spec.default;
        break;
      case "boolean":
        out[spec.id] = typeof v === "boolean" ? v : spec.default;
        break;
      case "enum":
        out[spec.id] =
          typeof v === "string" && spec.options.some((o) => o.value === v)
            ? v
            : spec.default;
        break;
      case "string":
        // String params are unbounded by definition — just coerce the type
        // and fall back to the spec default for a missing/non-string entry.
        out[spec.id] = typeof v === "string" ? v : spec.default;
        break;
      case "objectRef":
        // String → stored id (whether or not it still resolves; generators
        // must tolerate dangling refs via `ctx.resolveObject` returning
        // null). `null` / anything else → spec default. We deliberately
        // don't reconcile against the live scene here: the scene may not
        // be fully populated yet at decode time, and a ref that resolves
        // on the next rebuild is cheaper than eagerly dropping it.
        out[spec.id] = typeof v === "string" && v.length > 0 ? v : spec.default;
        break;
      case "list": {
        if (!Array.isArray(v)) {
          out[spec.id] = [...spec.default];
          break;
        }
        const items: GeneratorListItem[] = [];
        for (const rawItem of v) {
          if (!rawItem || typeof rawItem !== "object") continue;
          const rec = rawItem as {
            id?: unknown;
            itemType?: unknown;
            params?: unknown;
          };
          if (typeof rec.itemType !== "string") continue;
          const itemType = spec.itemTypes.find((it) => it.id === rec.itemType);
          if (!itemType) continue;
          const id =
            typeof rec.id === "string" && rec.id.length > 0
              ? rec.id
              : createListItemId();
          const nestedRaw =
            rec.params && typeof rec.params === "object"
              ? (rec.params as Record<string, unknown>)
              : {};
          items.push({
            id,
            itemType: itemType.id,
            params: decodeParams(itemType.paramSpecs, nestedRaw),
          });
        }
        out[spec.id] = items;
        break;
      }
      case "section": {
        Object.assign(out, decodeParams(spec.params, source));
        break;
      }
    }
  }
  return out;
}

/**
 * Decode a serialized generator ref, reconciling it with the generator
 * registry:
 *
 *  - Unknown `generatorId` → return `undefined`. The object loads as a
 *    fully baked mesh; no information is lost visually because the
 *    serialized geometry is still present.
 *  - Known id: params (including nested list items) are recursively
 *    reconciled against the generator's spec tree via `decodeParams`.
 *
 * On load we never automatically re-run `build` — we trust the serialized
 * geometry and only rebuild when the user edits a param in the sidebar.
 */
function decodeGeneratorRef(
  s: SerializedGeneratorRef | null | undefined,
): GeneratorRef | undefined {
  if (!s) return undefined;
  const gen = getGenerator(s.generatorId);
  if (!gen) return undefined;
  // Single-mesh only — `SceneObject.generator` never carries a compound
  // ref. If the generator's build output shape has changed under an old
  // save (mesh → group), the ref drops and the object loads as baked.
  const ref: GeneratorRef = {
    generatorId: s.generatorId,
    params: decodeParams(gen.paramSpecs, s.params ?? null),
  };
  // Signature is opaque to the codec — round-tripped verbatim. On a stale
  // save (signature formula changed between versions), the first post-load
  // rebuild will mismatch and regenerate geometry once; subsequent edits
  // then hit the fast path against the freshly-stamped token.
  if (typeof s.geometrySignature === "string") {
    ref.geometrySignature = s.geometrySignature;
  }
  return ref;
}

/**
 * Decode a serialized collection-owned compound generator ref. Returns
 * `undefined` when the generator id is unknown or its `roleToObjectId`
 * map references no living objects — the collection then loads as a
 * plain baked group.
 */
export function decodeCollectionGeneratorRef(
  s:
    | {
        generatorId: string;
        params: Record<string, unknown> | null | undefined;
        roleToObjectId: Record<string, string> | null | undefined;
      }
    | null
    | undefined,
): CollectionGeneratorRef | undefined {
  if (!s) return undefined;
  const gen = getGenerator(s.generatorId);
  if (!gen) return undefined;
  const roleToObjectId: { [role: string]: string } = {};
  if (s.roleToObjectId) {
    for (const [role, objectId] of Object.entries(s.roleToObjectId)) {
      if (typeof objectId === "string" && objectId.length > 0) {
        roleToObjectId[role] = objectId;
      }
    }
  }
  return {
    generatorId: s.generatorId,
    params: decodeParams(gen.paramSpecs, s.params ?? null),
    roleToObjectId,
  };
}

// ---------------------------------------------------------------------------
// Topology codec
// ---------------------------------------------------------------------------

function encodeTopology(t: PolygonTopology): SerializedTopology {
  return {
    offsets: typedArrayToBase64(t.offsets),
    corners: typedArrayToBase64(t.corners),
    edges: typedArrayToBase64(t.edges),
    cornerEdges: typedArrayToBase64(t.cornerEdges),
    edgeFlags: uint8ArrayToBase64(t.edgeFlags),
    // Omit-when-empty: the field is absent from the runtime topology when no
    // edge carries a non-zero weight. Emit `null` so JSON stays explicit.
    edgeCreases: t.edgeCreases ? typedArrayToBase64(t.edgeCreases) : null,
  };
}

function decodeTopology(t: SerializedTopology): PolygonTopology {
  const decoded: PolygonTopology = {
    offsets: base64ToUint32Array(t.offsets),
    corners: base64ToUint32Array(t.corners),
    edges: base64ToUint32Array(t.edges),
    cornerEdges: base64ToUint32Array(t.cornerEdges),
    edgeFlags: base64ToUint8Array(t.edgeFlags),
  };
  // Legacy scenes serialized before the field existed have no `edgeCreases`
  // key; null means "explicitly empty" in the new format. Both decode to
  // `undefined` at runtime, matching the omit-when-empty invariant.
  if (t.edgeCreases) {
    decoded.edgeCreases = base64ToFloat32Array(t.edgeCreases);
  }
  return decoded;
}

// ---------------------------------------------------------------------------
// SceneObject codec
// ---------------------------------------------------------------------------

/**
 * Encode a live `SceneObject` to its serialized form.
 *
 * The geometry block (vertices, indices, loopNormals, topology, uvs,
 * baseVertices) is encoded as a unit and emitted as nulls when the object is
 * a mannequin backed by a GLB model — its geometry is rebuilt at decode time.
 */
export function encodeSceneObject(o: SceneObject): SerializedSceneObject {
  const omitGeo = mannequinCanOmitGeometry(o);
  return {
    id: o.id,
    name: o.name,
    objectKind: o.objectKind,
    position: [o.position[0], o.position[1], o.position[2]],
    rotation: [o.rotation[0], o.rotation[1], o.rotation[2]],
    scale: [o.scale[0], o.scale[1], o.scale[2]],

    vertices: omitGeo ? null : typedArrayToBase64(o.vertices),
    indices: omitGeo ? null : typedArrayToBase64(o.indices),
    loopNormals: omitGeo ? null : typedArrayToBase64(o.loopNormals),
    topology: omitGeo ? null : encodeTopology(o.topology),
    uvs: omitGeo ? null : o.uvs ? typedArrayToBase64(o.uvs) : null,
    baseVertices: omitGeo
      ? null
      : o.baseVertices
        ? typedArrayToBase64(o.baseVertices)
        : null,

    skinRadii: o.skinRadii ? typedArrayToBase64(o.skinRadii) : null,

    vertexGroups:
      o.vertexGroups && o.vertexGroups.length > 0
        ? o.vertexGroups.map((g) => ({
            id: g.id,
            name: g.name,
            indices: [...g.indices].sort((a, b) => a - b),
          }))
        : null,

    materialSlots: o.materialSlots.map((slot) => ({
      id: slot.id,
      materialId: slot.materialId,
    })),
    shading: o.shading,
    autoSmoothAngle: o.autoSmoothAngle ?? null,
    faceAttributes: encodeFaceAttributes(o.faceAttributes),

    light: o.light ? { ...o.light } : null,
    mannequinModelId: o.mannequinModelId ?? null,
    originOffset: o.originOffset
      ? [o.originOffset[0], o.originOffset[1], o.originOffset[2]]
      : [0, 0, 0],
    curveData: o.curveData ? structuredClone(o.curveData) : null,
    imagePlane: o.imagePlane
      ? {
          imageKey: o.imagePlane.imageKey,
          intrinsicWidth: o.imagePlane.intrinsicWidth,
          intrinsicHeight: o.imagePlane.intrinsicHeight,
        }
      : null,

    generator: o.generator ? encodeGeneratorRef(o.generator) : null,
  };
}

/**
 * Decode a serialized object's material slots. Every `SerializedSceneObject`
 * must carry at least one slot — a missing or empty list is a hard error
 * (the file is malformed).
 */
function decodeMaterialSlots(s: SerializedSceneObject): MaterialSlot[] {
  if (!s.materialSlots || s.materialSlots.length === 0) {
    throw new Error(`SerializedSceneObject "${s.id}" has no materialSlots`);
  }
  return s.materialSlots.map((slot) => ({
    id: slot.id,
    materialId: slot.materialId,
  }));
}

/**
 * Encode a `FaceAttributeTable` for persistence. Returns `null` for the
 * normalized "empty" form so the JSON omits the field for objects with no
 * per-face data.
 */
function encodeFaceAttributes(
  table: FaceAttributeTable | undefined,
): SerializedFaceAttributeTable | null {
  const normalized = normalizeFaceAttributes(table);
  if (!normalized) return null;
  return {
    materialIndex: normalized.materialIndex
      ? typedArrayToBase64(normalized.materialIndex)
      : null,
    shading: normalized.shading ? uint8ArrayToBase64(normalized.shading) : null,
    groups:
      normalized.groups && normalized.groups.length > 0
        ? normalized.groups.map((g) => ({
            id: g.id,
            name: g.name,
            faces: [...g.faces].sort((a, b) => a - b),
          }))
        : null,
  };
}

/**
 * Decode a serialized `FaceAttributeTable` back to its in-memory form.
 * Returns `undefined` for null / empty payloads to preserve the
 * omit-when-empty invariant on `SceneObject.faceAttributes`.
 */
function decodeFaceAttributes(
  s: SerializedFaceAttributeTable | null | undefined,
): FaceAttributeTable | undefined {
  if (!s) return undefined;
  const out: FaceAttributeTable = {};
  if (s.materialIndex) out.materialIndex = base64ToUint16Array(s.materialIndex);
  if (s.shading) out.shading = base64ToUint8Array(s.shading);
  if (s.groups && s.groups.length > 0) {
    out.groups = s.groups.map((g) => ({
      id: g.id,
      name: g.name,
      faces: new Set(g.faces),
    }));
  }
  return normalizeFaceAttributes(out);
}

/** Empty geometry placeholder used for GLB-backed mannequins before hydration. */
function emptyTopology(): PolygonTopology {
  return {
    offsets: new Uint32Array([0]),
    corners: new Uint32Array(0),
    edges: new Uint32Array(0),
    cornerEdges: new Uint32Array(0),
    edgeFlags: new Uint8Array(0),
  };
}

/**
 * Decode a serialized scene object back to a live `SceneObject`.
 *
 * Curve objects rebuild their mesh from `curveData` after decoding, so
 * the deserialized geometry buffers are replaced with freshly generated
 * ones (matching the live editing flow).
 */
export function decodeSceneObject(s: SerializedSceneObject): SceneObject {
  const hasGeo = s.topology != null;
  const obj: SceneObject = {
    id: s.id,
    name: s.name,
    objectKind: s.objectKind,
    position: [s.position[0], s.position[1], s.position[2]],
    rotation: [s.rotation[0], s.rotation[1], s.rotation[2]],
    scale: [s.scale[0], s.scale[1], s.scale[2]],
    vertices: hasGeo ? base64ToFloat32Array(s.vertices!) : new Float32Array(0),
    indices: hasGeo
      ? asRenderTriangleIndices(base64ToUint32Array(s.indices!))
      : EMPTY_RENDER_TRIANGLE_INDICES,
    loopNormals: asLoopNormals(
      hasGeo ? base64ToFloat32Array(s.loopNormals!) : new Float32Array(0),
    ),
    topology: hasGeo ? decodeTopology(s.topology!) : emptyTopology(),
    uvs: s.uvs ? base64ToFloat32Array(s.uvs) : undefined,
    baseVertices: s.baseVertices
      ? base64ToFloat32Array(s.baseVertices)
      : undefined,
    skinRadii: s.skinRadii ? base64ToFloat32Array(s.skinRadii) : undefined,
    vertexGroups: s.vertexGroups
      ? s.vertexGroups.map((g) => ({
          id: g.id,
          name: g.name,
          indices: new Set(g.indices),
        }))
      : undefined,
    materialSlots: decodeMaterialSlots(s),
    shading: s.shading,
    autoSmoothAngle: s.autoSmoothAngle ?? undefined,
    faceAttributes: decodeFaceAttributes(s.faceAttributes),
    light: s.light ? { ...s.light } : undefined,
    mannequinModelId: s.mannequinModelId ?? undefined,
    originOffset:
      s.originOffset[0] === 0 &&
      s.originOffset[1] === 0 &&
      s.originOffset[2] === 0
        ? undefined
        : [s.originOffset[0], s.originOffset[1], s.originOffset[2]],
    curveData: s.curveData ? structuredClone(s.curveData) : undefined,
    imagePlane: s.imagePlane
      ? {
          imageKey: s.imagePlane.imageKey,
          intrinsicWidth: s.imagePlane.intrinsicWidth,
          intrinsicHeight: s.imagePlane.intrinsicHeight,
        }
      : undefined,
    generator: decodeGeneratorRef(s.generator ?? null),
  };

  // Curves rebuild their cached mesh from the authored control points.
  if (obj.curveData) {
    const geo = generateCurveGeometry(obj.curveData);
    return normalizeSceneObjectGeometry({
      ...obj,
      vertices: geo.vertices,
      indices: geo.indices,
      loopNormals: geo.loopNormals,
      topology: geo.topology,
      uvs: geo.uvs,
    });
  }
  return normalizeSceneObjectGeometry(obj);
}

// ---------------------------------------------------------------------------
// Material data block codec
// ---------------------------------------------------------------------------

import type { MaterialDataBlock } from "../../modeling/types.ts";
import type { SerializedMaterialDataBlock } from "./types.ts";

export function encodeMaterialDataBlock(
  m: MaterialDataBlock,
): SerializedMaterialDataBlock {
  return {
    id: m.id,
    name: m.name,
    properties: isDefaultMaterial(m.properties) ? null : { ...m.properties },
  };
}

export function decodeMaterialDataBlock(
  s: SerializedMaterialDataBlock,
): MaterialDataBlock {
  return {
    id: s.id,
    name: s.name,
    properties: s.properties ? { ...s.properties } : { ...DEFAULT_MATERIAL },
  };
}
